/**
 * i18n manager — i18next + LanguageDetector, vanilla ESM.
 *
 * - Languages: 'pt' (default) | 'en'
 * - Namespaces: por seção do one-pager
 * - Detection: localStorage 'qualiapps-language' → navigator.language → fallback 'pt'
 * - Persistência: localStorage[STORAGE_KEY] guarda 'pt' | 'en'
 * - <html lang> sincroniza com o idioma ativo
 */

import i18next, { type i18n } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// ---------- Locales (resources) ----------
// Cada namespace é importado do JSON correspondente. Os JSONs começam vazios
// e são populados em cada section task. `?import=default` garante que Vite
// importe o JSON inteiro como default export sob TypeScript strict.

import ptCommon from '../locales/pt/common.json';
import ptNavigation from '../locales/pt/navigation.json';
import ptFooter from '../locales/pt/footer.json';
import ptHero from '../locales/pt/hero.json';
import ptFamilia from '../locales/pt/familia.json';
import ptEstimator from '../locales/pt/estimator.json';
import ptServices from '../locales/pt/services.json';
import ptAbout from '../locales/pt/about.json';
import ptProcess from '../locales/pt/process.json';
import ptQualiobra from '../locales/pt/qualiobra.json';
import ptPortfolio from '../locales/pt/portfolio.json';
import ptTeam from '../locales/pt/team.json';
import ptTestimonials from '../locales/pt/testimonials.json';
import ptAulas from '../locales/pt/aulas.json';
import ptFaq from '../locales/pt/faq.json';
import ptContact from '../locales/pt/contact.json';
import ptPrivacy from '../locales/pt/privacy.json';
import ptWaitlist from '../locales/pt/waitlist.json';

import enCommon from '../locales/en/common.json';
import enNavigation from '../locales/en/navigation.json';
import enFooter from '../locales/en/footer.json';
import enHero from '../locales/en/hero.json';
import enFamilia from '../locales/en/familia.json';
import enEstimator from '../locales/en/estimator.json';
import enServices from '../locales/en/services.json';
import enAbout from '../locales/en/about.json';
import enProcess from '../locales/en/process.json';
import enQualiobra from '../locales/en/qualiobra.json';
import enPortfolio from '../locales/en/portfolio.json';
import enTeam from '../locales/en/team.json';
import enTestimonials from '../locales/en/testimonials.json';
import enAulas from '../locales/en/aulas.json';
import enFaq from '../locales/en/faq.json';
import enContact from '../locales/en/contact.json';
import enPrivacy from '../locales/en/privacy.json';
import enWaitlist from '../locales/en/waitlist.json';

export const SUPPORTED_LNGS = ['pt', 'en'] as const;
export type Lng = (typeof SUPPORTED_LNGS)[number];

export const NAMESPACES = [
  'common',
  'navigation',
  'footer',
  'hero',
  'familia',
  'estimator',
  'services',
  'about',
  'process',
  'qualiobra',
  'portfolio',
  'team',
  'testimonials',
  'aulas',
  'faq',
  'contact',
  'privacy',
  'waitlist',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

const STORAGE_KEY = 'qualiapps-language';

const resources = {
  pt: {
    common: ptCommon,
    navigation: ptNavigation,
    footer: ptFooter,
    hero: ptHero,
    familia: ptFamilia,
    estimator: ptEstimator,
    services: ptServices,
    about: ptAbout,
    process: ptProcess,
    qualiobra: ptQualiobra,
    portfolio: ptPortfolio,
    team: ptTeam,
    testimonials: ptTestimonials,
    aulas: ptAulas,
    faq: ptFaq,
    contact: ptContact,
    privacy: ptPrivacy,
    waitlist: ptWaitlist,
  },
  en: {
    common: enCommon,
    navigation: enNavigation,
    footer: enFooter,
    hero: enHero,
    familia: enFamilia,
    estimator: enEstimator,
    services: enServices,
    about: enAbout,
    process: enProcess,
    qualiobra: enQualiobra,
    portfolio: enPortfolio,
    team: enTeam,
    testimonials: enTestimonials,
    aulas: enAulas,
    faq: enFaq,
    contact: enContact,
    privacy: enPrivacy,
    waitlist: enWaitlist,
  },
} as const;

function isLng(value: unknown): value is Lng {
  return typeof value === 'string' && (SUPPORTED_LNGS as readonly string[]).includes(value);
}

// ---------- Public API ----------

let initialized = false;
let initPromise: Promise<void> | null = null;

/**
 * Inicializa i18next com LanguageDetector. Idempotente.
 * Retorna Promise que resolve quando a primeira tradução está disponível.
 */
export function initI18n(): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = i18next
    .use(LanguageDetector)
    .init({
      resources,
      supportedLngs: SUPPORTED_LNGS as unknown as string[],
      fallbackLng: 'pt',
      defaultNS: 'common',
      ns: NAMESPACES as unknown as string[],
      // Convenção do projeto: chaves usam `<ns>.<key>` (e.g. `hero.badge`).
      // i18next default é `nsSeparator: ':'` + `keySeparator: '.'` — sem este
      // override, `t('hero.badge')` resolveria como path aninhado no NS default,
      // retornando a chave nua. JSONs são flat — `keySeparator: false` evita
      // que pontos dentro do nome da chave sejam interpretados como path.
      nsSeparator: '.',
      keySeparator: false,
      interpolation: { escapeValue: false }, // não-React, HTML é tratado por nós
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: STORAGE_KEY,
        caches: ['localStorage'],
      },
      returnNull: false,
      returnEmptyString: false,
      // Em dev, console.warn para chaves órfãs ajuda a achar gaps de tradução.
      saveMissing: false,
      missingKeyHandler: import.meta.env.DEV
        ? (lngs, ns, key) => {
            // eslint-disable-next-line no-console
            console.warn(`[i18n] missing key "${ns}:${key}" for [${lngs.join(', ')}]`);
          }
        : undefined,
    })
    .then(() => {
      initialized = true;
      // Garante que <html lang> reflete o idioma efetivo
      const lng = getLanguage();
      if (typeof document !== 'undefined') {
        document.documentElement.lang = lng === 'pt' ? 'pt-BR' : 'en';
      }
    });

  return initPromise;
}

/**
 * Tradução. Use sempre via `applyI18nToDom` para elementos HTML.
 */
export function t(key: string, opts?: Record<string, unknown>): string {
  if (!initialized) {
    // Em pré-init, devolver a chave nua é melhor que crashear.
    return key;
  }
  return i18next.t(key, opts as never) as unknown as string;
}

/**
 * Lê o idioma EFETIVO (já normalizado para 'pt' | 'en').
 */
export function getLanguage(): Lng {
  const lng = i18next.language;
  if (isLng(lng)) return lng;
  // Normaliza 'pt-BR' → 'pt', 'en-US' → 'en'.
  const base = (lng || '').split('-')[0];
  return isLng(base) ? base : 'pt';
}

/**
 * Troca idioma. Persiste em localStorage + atualiza `<html lang>` +
 * dispara `languageChanged` (a infra DOM em `i18n-dom.ts` reage).
 */
export async function setLanguage(lng: Lng): Promise<void> {
  if (!isLng(lng)) return;
  await i18next.changeLanguage(lng);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng === 'pt' ? 'pt-BR' : 'en';
  }
}

/**
 * Registra callback chamado em toda troca de idioma. Retorna cleanup.
 */
export function onLanguageChange(cb: (lng: Lng) => void): () => void {
  const handler = (lng: string): void => {
    if (isLng(lng)) cb(lng);
    else {
      const base = lng.split('-')[0];
      if (isLng(base)) cb(base);
    }
  };
  i18next.on('languageChanged', handler);
  return () => {
    i18next.off('languageChanged', handler);
  };
}

/**
 * Expõe a instância i18next (raramente necessário; preferir API tipada acima).
 */
export function getI18nInstance(): i18n {
  return i18next;
}
